// src/modules/units/tyres/shared/tyre-actions.js
//
// The canonical list of tyre actions, split into the two things the dashboard
// actually offers. Both the dashboard and the controller's routing read from
// here, so a label, icon or destination is defined once.
//
// Why two lists rather than one ring of eight: the ring mixed "do something to a
// tyre" with "look something up" and gave both the same weight, which asked the
// reader to learn a layout before they could use it. Management's read was that
// this would not survive contact with our less confident users, and they were
// right. The lifecycle actions are now big, ordered the way a tyre actually
// travels, and each carries a plain-English line; lookup is a quieter row below.
//
// LIFECYCLE ORDER IS THE ARRAY ORDER. New -> Fit -> Inspect -> Repair -> Retread
// -> Retire, reading left to right, top to bottom. It is deliberately not
// numbered: position teaches the sequence, but numbers would imply you must work
// through it in order, and most days the honest answer is "I just inspected this
// one".
//
// `tone` names a colour token in tyres.css (--tm-tone-*), chosen as the status
// the tyre LANDS IN after the action — New leaves a tyre in stock, Fit leaves it
// fitted, and so on. That way the button teaches the badge the user will meet on
// that tyre later, instead of the colours being decoration.
//
// `blurb` is end-user copy for any scaffolded screen: plain, active, describes
// what the screen will let you do (not how it's built).

export const TYRE_LIFECYCLE_ACTIONS = [
  {
    id: 'new', label: 'New', sub: 'Add a tyre to stock', tone: 'stock', icon: 'new',
    blurb: 'Add a brand-new tyre — scan or enter its ID and record its details.'
  },
  {
    id: 'fit', label: 'Fit Tyre to a Sensor', sub: 'Associate a sensor with a tyre', tone: 'fitted', icon: 'fit',
    blurb: 'Fit a tyre to a wheel sensor or move a tyre to another sensor, and record the position change.'
  },
  {
    id: 'inspect', label: 'Inspect', sub: 'Measure tread depth and assess wear pattern', tone: 'inspect', icon: 'inspect',
    blurb: 'Log an inspection — tread depth, wear pattern and condition.'
  },
  {
    id: 'repair', label: 'Repair', sub: 'Log a repair', tone: 'repair', icon: 'repair',
    blurb: 'Send a tyre for repair and track it until it returns to service.'
  },
  {
    id: 'retread', label: 'Retread', sub: 'Dispatch and receive batches', tone: 'retread', icon: 'retread',
    blurb: 'Send tyres to a supplier for retread in a batch, and receive them back into stock.'
  },
  {
    // The one irreversible action, so it is styled as an outline rather than a
    // filled tile: different in kind, not just different in colour. That also
    // keeps it from reading as a second slate tile opposite New.
    id: 'retire', label: 'Retire', sub: 'Scrapped, sold or missing', variant: 'outline', icon: 'retire',
    blurb: 'Take a tyre out of the fleet and record why it left.'
  }
];

// The dashboard now separates read/reporting destinations from customer setup so
// the lower row does not become a miscellaneous bucket as more admin functions
// are added.
export const TYRE_RECORD_ACTIONS = [
  { id: 'register', label: 'Tyre register', icon: 'register', blurb: 'Browse, search and add tyres, and open any tyre to see its full history.' },
  { id: 'reports', label: 'Reports', icon: 'reports', blurb: 'Cost, wear and lifecycle reports across the fleet, ready to export.' },
  // `adminOnly`: shown only to users who pass canArchiveTyres() (tyre-access.js).
  // The route is guarded too, so a hidden tile cannot be reached another way.
  { id: 'archived', label: 'Archived tyres', icon: 'archive', adminOnly: true, blurb: 'Tyres an administrator has archived — hidden from the register, reports and exports — and restore any of them.' }
];

export const TYRE_SETUP_ACTIONS = [
  { id: 'yards', label: 'Yards', icon: 'yards', blurb: 'Create your yards and switch between them — the whole tyre area follows the yard you pick.' },
  { id: 'suppliers', label: 'Suppliers', icon: 'supplier', blurb: 'Add and maintain the tyre suppliers available to the active customer.' },
  { id: 'catalogue', label: 'Tyre catalogue', icon: 'catalogue', blurb: 'Maintain the manufacturer, model and tread combinations used by the active customer.' },
  { id: 'import', label: 'Import tyres', icon: 'import', blurb: 'Bring a list of tyres into a yard from an Excel or CSV spreadsheet.' }
];

// Backward-compatible combined list for code that only needs to look up an id.
export const TYRE_LOOKUP_ACTIONS = [...TYRE_RECORD_ACTIONS, ...TYRE_SETUP_ACTIONS];

// Everything, for id lookup. Stock / Depot is gone from the dashboard by
// decision — the register with a status filter covers it — but the route still
// exists, so nothing that links to it breaks.
export const TYRE_ACTIONS = [...TYRE_LIFECYCLE_ACTIONS, ...TYRE_LOOKUP_ACTIONS];

const BY_ID = Object.fromEntries(TYRE_ACTIONS.map((a) => [a.id, a]));

export function getTyreAction(id) {
  return BY_ID[id] || null;
}
